import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { setAuthData } from '../../utils/auth';
import { authAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface LoginFormData {
  email: string;
  password: string;
}

export const UserLogin: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.password) {
      toast.error('Password is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const data = await authAPI.login(formData.email, formData.password);

      // Check if email verification is required
      if ('requiresVerification' in data) {
        toast.error(data.message || 'Please verify your email before logging in');
        navigate('/verify-email', { 
          state: { 
            userId: data.userId, 
            email: data.email 
          } 
        });
        return;
      }

      // Store auth data (TypeScript now knows this is AuthData)
      setAuthData({
        token: data.token,
        user: data.user
      });

      toast.success('Login successful!');
      
      // Check if there's a saved search state
      const returnPath = sessionStorage.getItem('returnPath');
      
      if (returnPath) {
        // Don't clear sessionStorage here - let SearchPanel handle it
        // Navigate to the return path (SearchPanel will restore from sessionStorage)
        navigate(returnPath);
      } else {
        // Navigate to search page normally
        navigate('/search');
      }

    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-[#63B56F] rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access unlimited solar panel searches
          </p>
        </div>

        <motion.form
          className="mt-8 space-y-6 bg-white rounded-xl shadow-lg p-8"
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none relative block w-full px-3 py-3 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#63B56F] focus:border-[#63B56F] focus:z-10 sm:text-sm"
                  placeholder="Enter your email address"
                />
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none relative block w-full px-3 py-3 pl-10 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-[#63B56F] focus:border-[#63B56F] focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  className="absolute right-3 top-3 h-4 w-4 text-gray-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-[#63B56F] hover:bg-[#4A9B57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#63B56F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-[#63B56F] hover:text-[#4A9B57]"
              >
                Forgot your password?
              </button>
            </p>
            
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => navigate('/user/signup')}
                className="font-medium text-[#63B56F] hover:text-[#4A9B57]"
              >
                Create one here
              </button>
            </p>
            
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="hover:text-[#63B56F]"
              >
                Admin Login
              </button>
              <span>•</span>
              <button
                type="button"
                onClick={() => navigate('/supplier/login')}
                className="hover:text-[#63B56F]"
              >
                Supplier Login
              </button>
            </div>
          </div>
        </motion.form>

        {/* Benefits Section */}
        <motion.div 
          className="bg-white rounded-xl shadow-lg p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Why create an account?
          </h3>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-[#63B56F] rounded-full"></div>
              <p className="text-sm text-gray-600">Unlimited solar panel searches</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-[#63B56F] rounded-full"></div>
              <p className="text-sm text-gray-600">Access detailed supplier contact information</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-[#63B56F] rounded-full"></div>
              <p className="text-sm text-gray-600">Save and organize your searches</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-[#63B56F] rounded-full"></div>
              <p className="text-sm text-gray-600">Request quotes directly from suppliers</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};